const resources = {
  "en": {
    "accountDeletion": {
      "title": "Delete account",
      "intro": "Request the permanent removal of your Finance workspace.",
      "overviewTitle": "Before you submit the request",
      "overviewDescription": "This page explains what will be removed, what happens to your subscription, and how to confirm the request safely.",
      "description": "We will remove your stored finance records and workflows when the deletion finishes. This action cannot be undone.",
      "retention": "We remove your profile and contact details from our systems. We only keep non-identifying deletion metadata required for compliance.",
      "subscriptionTermination": "Submitting this deletion will immediately end your current subscription. We do not provide refunds for unused time or credits.",
      "supportContact": "If extraordinary circumstances apply, contact our support team before submitting so we can review options together.",
      "categoriesTitle": "What will be deleted",
      "categoriesDescription": "These data groups are removed when the account deletion is completed.",
      "categories": {
        "transactions": "Transactions, transfers, and cash flow analytics",
        "accounts": "Accounts, balances, and debt tracking",
        "budgets": "Budgets, spending groups, and recurring plans",
        "goals": "Goals, vaults, and contract schedules",
        "forecasts": "Forecast adjustments, AI insights, and GPT analyses",
        "tasks": "Tasks, deadlines, and automation history",
        "security": "Login sessions, two-factor records, and passkeys",
        "invites": "Invites, rewards, promo redemptions, and community history"
      },
      "dataExport": {
        "title": "Download your data",
        "description": "Create a self-service JSON export before deleting your account or whenever you need a copy.",
        "body": "The export includes your account data and financial records. Internal security, audit, billing, anti-abuse, backup records, and secrets are excluded or minimized.",
        "button": "Request and download data export",
        "success": "Data export downloaded.",
        "errorTitle": "Unable to download data export",
        "error": "Unable to create or download your data export. Please try again."
      },
      "status": {
        "loading": "Checking account deletion status…",
        "error": "Unable to load account deletion status.",
        "requested": "Your deletion request is awaiting email confirmation. Follow the link we sent to continue.",
        "pending": "Your deletion request is being processed. We will email you once it is complete.",
        "completed": "Your account has already been deleted. You can safely close this page.",
        "generic": "Current deletion status: {{status}}"
      },
      "callToAction": {
        "title": "Confirm the request",
        "description": "You must confirm that you understand the impact before we let you submit the deletion request.",
        "checkbox": "I understand this will permanently delete my data.",
        "button": "Request account deletion",
        "buttonPending": "Deletion request pending",
        "refreshStatus": "Refresh status",
        "success": "Deletion request submitted. Check your email to confirm.",
        "error": "Unable to submit deletion request. Please try again."
      }
    }
  },
  "uk": {
    "accountDeletion": {
      "title": "Видалити акаунт",
      "intro": "Запит на остаточне видалення вашого робочого простору Finance.",
      "overviewTitle": "Що потрібно знати перед поданням",
      "overviewDescription": "На цій сторінці пояснюється, що саме буде видалено, що станеться з підпискою та як безпечно підтвердити запит.",
      "description": "Ми видалимо ваші фінансові записи та робочі процеси після завершення видалення. Цю дію неможливо скасувати.",
      "retention": "Ми видаляємо профіль і контактні дані з наших систем. Зберігаємо лише неідентифікаційні дані про видалення для відповідності вимогам.",
      "subscriptionTermination": "Подання цього запиту негайно завершить вашу поточну підписку. Ми не повертаємо кошти за невикористаний час або кредити.",
      "supportContact": "Якщо є виняткові обставини, зверніться до нашої служби підтримки перед поданням запиту, щоб ми могли разом розглянути варіанти.",
      "categoriesTitle": "Що буде видалено",
      "categoriesDescription": "Ці групи даних буде видалено після завершення видалення акаунта.",
      "categories": {
        "transactions": "Транзакції, перекази та аналітика грошових потоків",
        "accounts": "Рахунки, баланси та облік боргів",
        "budgets": "Бюджети, групи витрат і регулярні плани",
        "goals": "Цілі, сховища та графіки контрактів",
        "forecasts": "Переоцінки прогнозів, AI-інсайти та GPT-аналізи",
        "tasks": "Завдання, дедлайни та історія автоматизацій",
        "security": "Сесії входу, двофакторні записи та ключі доступу",
        "invites": "Запрошення, винагороди, промо-редемпшени та історія спільноти"
      },
      "dataExport": {
        "title": "Завантажити свої дані",
        "description": "Створіть self-service JSON-експорт перед видаленням акаунта або коли потрібна копія.",
        "body": "Експорт містить дані акаунта та фінансові записи. Внутрішні записи безпеки, аудиту, білінгу, захисту від зловживань, резервні копії та секрети виключаються або мінімізуються.",
        "button": "Запросити й завантажити експорт",
        "success": "Експорт даних завантажено.",
        "errorTitle": "Не вдалося завантажити експорт даних",
        "error": "Не вдалося створити або завантажити експорт даних. Будь ласка, спробуйте ще раз."
      },
      "status": {
        "loading": "Перевіряємо статус видалення акаунта…",
        "error": "Не вдалося завантажити статус видалення акаунта.",
        "requested": "Ваш запит на видалення очікує підтвердження електронною поштою. Перейдіть за посиланням, яке ми надіслали.",
        "pending": "Ваш запит на видалення обробляється. Ми повідомимо вас електронною поштою після завершення.",
        "completed": "Ваш акаунт уже видалено. Ви можете безпечно закрити цю сторінку.",
        "generic": "Поточний статус видалення: {{status}}"
      },
      "callToAction": {
        "title": "Підтвердіть запит",
        "description": "Перш ніж ми дозволимо надіслати запит, потрібно підтвердити, що ви розумієте наслідки.",
        "checkbox": "Я розумію, що це назавжди видалить мої дані.",
        "button": "Запросити видалення акаунта",
        "buttonPending": "Запит на видалення очікує",
        "refreshStatus": "Оновити статус",
        "success": "Запит на видалення надіслано. Перевірте пошту для підтвердження.",
        "error": "Не вдалося надіслати запит на видалення. Будь ласка, спробуйте ще раз."
      }
    }
  }
};

export default resources;
